import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, Globe, MapPin, Users, DollarSign, Calendar, Pencil, Trash2 } from "lucide-react";
import { format } from "date-fns";

interface AccountWithStats {
  id: string;
  name: string;
  industry: string | null;
  website: string | null;
  country: string | null;
  size_band: string | null;
  created_at: string;
  updated_at: string;
  contacts_count: number;
  deals_count: number;
  total_value: number;
}

interface AccountDetailSheetProps {
  account: AccountWithStats | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (account: AccountWithStats) => void;
  onDelete: (account: AccountWithStats) => void;
}

const sizeLabels: Record<string, string> = {
  enterprise: "Enterprise",
  mid_market: "Mid-Market",
  sme: "SME",
  micro: "Micro",
};

const sizeColors: Record<string, string> = {
  enterprise: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  mid_market: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  sme: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
  micro: "bg-green-500/10 text-green-400 border-green-500/20",
};

export function AccountDetailSheet({ account, open, onOpenChange, onEdit, onDelete }: AccountDetailSheetProps) {
  if (!account) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="pb-4 border-b border-border">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <SheetTitle className="text-xl">{account.name}</SheetTitle>
                <p className="text-sm text-muted-foreground">{account.industry || "No industry"}</p>
              </div>
            </div>
            <Badge variant="outline" className={sizeColors[account.size_band || "sme"]}>
              {sizeLabels[account.size_band || "sme"]}
            </Badge>
          </div>
        </SheetHeader>

        <div className="py-6 space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="glass rounded-lg p-3 text-center">
              <Users className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
              <p className="text-2xl font-bold">{account.contacts_count}</p>
              <p className="text-xs text-muted-foreground">Contacts</p>
            </div>
            <div className="glass rounded-lg p-3 text-center">
              <DollarSign className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
              <p className="text-2xl font-bold">{account.deals_count}</p>
              <p className="text-xs text-muted-foreground">Deals</p>
            </div>
            <div className="glass rounded-lg p-3 text-center">
              <DollarSign className="h-5 w-5 mx-auto mb-1 text-success" />
              <p className="text-2xl font-bold text-success">${account.total_value.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Total Value</p>
            </div>
          </div>

          {/* Details */}
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="w-full">
              <TabsTrigger value="details" className="flex-1">Details</TabsTrigger>
              <TabsTrigger value="activity" className="flex-1">Activity</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4 pt-4">
              {account.website && (
                <div className="flex items-center gap-3">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <a 
                    href={account.website.startsWith('http') ? account.website : `https://${account.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {account.website}
                  </a>
                </div>
              )}

              {account.country && (
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{account.country}</span>
                </div>
              )}

              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">
                  Created {format(new Date(account.created_at), "MMM d, yyyy")}
                </span>
              </div>

              <div className="pt-4 border-t border-border">
                <h4 className="text-sm font-medium mb-3">Actions</h4>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="gap-2"
                    onClick={() => onEdit(account)}
                  >
                    <Pencil className="h-4 w-4" />
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="gap-2 text-destructive hover:text-destructive"
                    onClick={() => onDelete(account)}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="activity" className="pt-4">
              <div className="text-center py-8 text-muted-foreground">
                <p>Activity timeline coming soon</p>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
}
